import { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { useU2101Context } from "../U2101Context";
import {
  buildLogicalUnitSavePayload,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  emptyLogicalUnit,
  luHandleAppiError,
} from "../U2101Service";
import type { LogicalUnit } from "../U2101Service";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { NS, U2101ROLES } from "../U2101";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2101LogicalUnitFormToolBar } from "./form/U2101LogicalUnit/U2101LogicalUnitFormToolBar";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import U2101LogicalUnitBody from "./form/U2101LogicalUnit/U2101LogicalUnitBody";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import U2101LogicalUnitActionsBody from "./form/U2101LogicalUnit/U2101LogicalUnitActionTab/U2101LogicalUnitActionBody";
import U2101LogicalUnitPersonalizationsBody from "./form/U2101LogicalUnit/U2101LogicalUnitPersonalizationTab/U2101LogicalUnitPersonalizationBody";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U2101LogicalUnitDeleteDlg from "./common/U2101LogicalUnitDeleteDlg";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U2101LogicalUnitFormExtendedHandlers extends U2000FormStateHandlersReturn<LogicalUnit> {
  addNewLogicalUnit: () => void;
  modifyLogicalUnit: (logicalUnit: LogicalUnit) => void;
  deleteLogicalUnit: (id: string) => Promise<void>;
}

const U2101LogicalUnitForm: React.FC = () => {
  // Parameters
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // Form state and reducer for u2101LogicalUnitBL
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // APA Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU2101Context();

  // Form Handlers and default methods
  const validateForm = (logicalUnit: LogicalUnit) => {
    const result: Record<string, string> = {};
    if (!logicalUnit.logicalUnitCode) result.logicalUnitCode = t("errLogicalUnitCodeRequired", { ns: NS });
    if (!logicalUnit.logicalUnitDescription) {
      result.logicalUnitDescription = t("errLogicalUnitDescriptionRequired", { ns: NS });
    }
    // At least one Logical Unit Type must be selected
    if (Array.isArray(logicalUnit.logicalUnitTypes) && logicalUnit.logicalUnitTypes.length === 0) {
      result.logicalUnitTypes = t("errLogicalUnitTypeRequired", { ns: NS });
    }
    return result;
  };

  const u2101LogicalUnitBL: U2000FormSBusinessHandlers<LogicalUnit> = {
    newRecord: () => {
      return emptyLogicalUnit;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (logicalUnit: LogicalUnit, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(logicalUnit);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(logicalUnit, editMode);
      } else {
        u2101LogicalUnitformStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const logicalUnit = u2101LogicalUnitformStateExtendedHandlers.getOldData() as LogicalUnit | null;
      if (logicalUnit) {
        // Met à false checkedId pour chaque action
        if (Array.isArray(logicalUnit.logicalUnitActions)) {
          logicalUnit.logicalUnitActions = logicalUnit.logicalUnitActions.map((action) => ({ ...action, idChecked: false }));
        }
        // Met à false checkedId pour chaque personnalization
        if (Array.isArray(logicalUnit.logicalUnitPersonalizations)) {
          logicalUnit.logicalUnitPersonalizations = logicalUnit.logicalUnitPersonalizations.map((pers: any) => ({ ...pers, idChecked: false }));
        }
        u2101LogicalUnitformStateHandlers.setData({ ...logicalUnit });
      }

      if (luStateHandlers.getActionOriginFrom() === "/U2101") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (logicalUnit) => {
      if (logicalUnit.id) {
        handleGet.getData(logicalUnit.id);
      }
    },

    // gestion du changement de page
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U2101");
    },
  };

  const u2101LogicalUnitformStateHandlers = useU2000FormStateHandlers<LogicalUnit>({
    formState,
    formStateDispatch,
    businessHandlers: u2101LogicalUnitBL,
  });

  // extended methods
  const u2101LogicalUnitformStateExtendedHandlers: U2101LogicalUnitFormExtendedHandlers = {
    ...u2101LogicalUnitformStateHandlers,

    addNewLogicalUnit: () => {
      u2101LogicalUnitformStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      //formStateHandlersBase.setData({ ...emptyLogicalUnit });
    },

    modifyLogicalUnit: () => {
      u2101LogicalUnitformStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },

    deleteLogicalUnit: async (_id: string) => {
      handleDelete.handleDelete();
    },
  };

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<LogicalUnit>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<LogicalUnit>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Get Handlers for Logical Unit
  const handleGet = useU2000GetHandlers<LogicalUnit & { id: string }>({
    doGetFn: (id: string) => {
      return doGetData(u2000AxiosAuthenticated, id);
    },
    onSuccess: (logicalUnit: LogicalUnit & { id: string }) => {
      gridStateHandlers.setDataById(logicalUnit.id, logicalUnit);
      u2101LogicalUnitformStateExtendedHandlers.setData(logicalUnit);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(logicalUnit.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([{ href: "/U2101", label: t("title", { ns: NS }) }, { label: logicalUnit.logicalUnitDescription + " (" + logicalUnit.logicalUnitCode + ")" }]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u2101LogicalUnitformStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<LogicalUnit>({
    doSaveFn: (logicalUnit: LogicalUnit, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildLogicalUnitSavePayload(logicalUnit));
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, logicalUnit.id, buildLogicalUnitSavePayload(logicalUnit));
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      return Promise.resolve(undefined);
    },
    onSuccess: (logicalUnit: LogicalUnit & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), logicalUnit]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(logicalUnit.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(logicalUnit.id, logicalUnit);
      }
      u2101LogicalUnitformStateExtendedHandlers.afterSaveData(logicalUnit);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U2101") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u2101LogicalUnitformStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u2101LogicalUnitformStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for Logical Unit
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u2101LogicalUnitformStateExtendedHandlers.getData()],
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["logicalUnitCode"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U2101", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Initialisation au montage et quand id change (ex. F5 sur /U2101/:id : id peut arriver après le 1er rendu).
  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u2101LogicalUnitformStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      u2101LogicalUnitformStateExtendedHandlers.setData({ ...emptyLogicalUnit });
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabAction",
      label: t("tabActions", { ns: NS }),
      content: <U2101LogicalUnitActionsBody u2101LogicalUnitformStateExtendedHandlers={u2101LogicalUnitformStateExtendedHandlers} />,
    },
    {
      id: "tabPersonlization",
      label: t("tabPersonalization", { ns: NS }),
      content: <U2101LogicalUnitPersonalizationsBody logicalUnitFormHandlers={u2101LogicalUnitformStateExtendedHandlers} />,
    },
    hasRole(U2101ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u2101LogicalUnitformStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U2101LogicalUnitAudit" />,
    },
  ].filter(Boolean);

  // Render
  return (
    <>
      <U2101LogicalUnitDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U2101LogicalUnitFormToolBar formStateHandlers={u2101LogicalUnitformStateExtendedHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u2101LogicalUnitformStateExtendedHandlers as any}
      >
        <U2101LogicalUnitBody formStateHandlers={u2101LogicalUnitformStateExtendedHandlers as any} t={t} />

        <U2000Tab
          className="mt-4"
          tabs={formTabsComponent}
          defaultTab="tabAction"
          activeTab={activeTab ?? "tabAction"}
          setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U2101LogicalUnitForm;
